import { GridOptions, RowNode, SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import { ChangeDetectorRef, Component, ElementRef, forwardRef } from '@angular/core';
import { UserAccess } from '@core/authorization/user-access';
import { DataGrid, DialogBase, DialogButton, Dialogs, IColDefPlus, M27Dialog } from '@m27/common';
import { IVolunteerReportCriteria, IVolunteerReportPermit } from '@reporting/interfaces';
import { ReportService } from '@reporting/services';
import { environment } from '@app/environment';
import { IPermitSearch } from '@shared/interfaces';
import { ICheckBox } from '@shared/interfaces/form-controls';
import { DataGridColumns } from '@shared/models';
import { Globals } from '@shared/models/globals';
import { PermitService } from '@shared/services';
import { Subscription, timer } from 'rxjs';

@Component({
    selector: 'reporting-dialog-volunteer-report-criteria',
    templateUrl: './dialog-volunteer-report-criteria.component.html',
    styleUrls: ['./dialog-volunteer-report-criteria.component.scss'],
    providers: [{ provide: DialogBase, useExisting: forwardRef(() => VolunteerReportCriteriaDialogComponent) }]
})
export class VolunteerReportCriteriaDialogComponent extends DialogBase implements M27Dialog {
    public gridOptions: GridOptions;
    public totalRowCount = 0;
    public userAccess: UserAccess = Globals.UAC;
    public selectedRowCount = 0;
    public hasSearched = false;
    public isSearching = false;

    public availableCrops: ICheckBox[] = [
        { Name: 'Corn', Value: 'CRN', IsChecked: false, IsDisabled: false },
        { Name: 'Soybeans', Value: 'SOY', IsChecked: false, IsDisabled: false },
        { Name: 'Sorghum', Value: 'SORG', IsChecked: false, IsDisabled: false },
        { Name: 'Canola', Value: 'CANO', IsChecked: false, IsDisabled: false },
        { Name: 'Wheat', Value: 'WHE', IsChecked: false, IsDisabled: false },
        { Name: 'Cotton', Value: 'COT', IsChecked: false, IsDisabled: false }
    ];
    private numberOfYears = environment.env === 'PROD' ? 5 : 10;
    public availableYears: ICheckBox[] = [];
    public availableStatus: ICheckBox[] = [
        { Name: 'Valid', Value: 'VLD', IsChecked: false, IsDisabled: false },
        { Name: 'Expired', Value: 'EXP', IsChecked: false, IsDisabled: false }
    ];

    public reportIdJustCreated = 0;
    public showReportJustCreatedBadge = false;
    private showNewReportBadgeSubscription: Subscription;

    // this is only calls once for entire web session
    constructor(private permitService: PermitService, private reportService: ReportService, cd: ChangeDetectorRef, elRef: ElementRef) {
        super(cd, elRef);

        const startYear: number = (new Date()).getFullYear();

        for (let x = 0; x < this.numberOfYears; x++) {
            this.availableYears.push({ Name: `${startYear - x}`, Value: startYear - x, IsChecked: false, IsDisabled: false });
        }
    }

    // this is only calls once for entire web session
    public onDialogReady(): void {
        this.setupDataGrid();
    }

    private setupDataGrid(): void {
        this.gridOptions = DataGrid.GetDefaults(this, 'VOLUNTEER-REPORT-PERMITS', () => { }, 'PermitId');

        this.gridOptions.columnDefs = [
            DataGrid.ColumnBuilder()
                .ForRowCheckBox()
                .Build(),

            DataGrid.ColumnBuilder()
                .ForBoolean('GetCurrentData', 'Get Current Data', 100, 'Yes', 'No')
                .AllowEditing()
                .Tooltip('Get Current Data')
                .Build(),

            ...DataGridColumns.PermitSearchResults()
        ] as IColDefPlus[];

        this.gridOptions.onSelectionChanged = (event: SelectionChangedEvent) => {
            this.selectedRowCount = this.gridOptions.api.getSelectedRows().filter((row: RowNode) => {
                return !row.group;
            }).length;
        };
    }

    private setupButtons(): void {
        if (this.isBackButtonEnabled()) {
            this.leftButtons.push({
                IsCancel: true,
                Text: 'Back',
                Action: (dialogBase: DialogBase, isChecked?: boolean) => {
                    this.close();
                }
            } as DialogButton);
        }

        this.rightButtons.push({
            IsCancel: false,
            Text: 'Run Report (Stay)',
            IsDisabled: () => {
                return !this.validateCanRunReport();
            },
            Action: (dialogBase: DialogBase) => {
                this.runReport(true, false);
            }
        } as DialogButton);

        this.rightButtons.push({
            IsCancel: false,
            Text: 'Run Report (Go)',
            IsDisabled: () => {
                return !this.validateCanRunReport();
            },
            Action: (dialogBase: DialogBase) => {
                this.runReport(false, false);
            }
        } as DialogButton);

        this.rightButtons.push({
            IsCancel: false,
            Text: 'Run Efile Report (Stay)',
            IsDisabled: () => {
                return !this.validateCanRunReport();
            },
            Action: (dialogBase: DialogBase) => {
                this.runReport(true, true);
            }
        } as DialogButton);

        this.rightButtons.push({
            IsCancel: false,
            Text: 'Run Efile Report (Go)',
            IsDisabled: () => {
                return !this.validateCanRunReport();
            },
            Action: (dialogBase: DialogBase) => {
                this.runReport(false, true);
            }
        } as DialogButton);
    }

    private runReport(stay: boolean, isEfile: boolean): void {
        const getDraftDataPermits = this.gridOptions.api.getSelectedNodes().filter((value) => {
            return !value.data.GetCurrentData;
        });
        const getCurrentDataPermits = this.gridOptions.api.getSelectedNodes().filter((value) => {
            return value.data.GetCurrentData;
        });

        if (getDraftDataPermits.length > 0) {
            this.reportService.CheckForVolunteerDraftData({
                PermitIdsForCurrentData: [],
                PermitIdsForDraftData: [],
                NoDraftsPermit: [],
                PermitNamesForDraftData: getDraftDataPermits.map(row => row.data.AgencyName),
                PermitNamesForCurrentData: getCurrentDataPermits.map(row => row.data.AgencyName),
            } as IVolunteerReportCriteria)
                .subscribe((refinedCriteria: IVolunteerReportCriteria) => {

                    const delegate = () => {
                        this.reportService.RunVolunteerReport({
                            PermitIdsForCurrentData: this.gridOptions.api.getSelectedNodes().filter((value) => {
                                return refinedCriteria.PermitNamesForCurrentData.includes(value.data.AgencyName);
                            }).map(row => row.data.PermitId),
                            PermitIdsForDraftData: this.gridOptions.api.getSelectedNodes().filter((value) => {
                                return refinedCriteria.PermitNamesForDraftData.includes(value.data.AgencyName);
                            }).map(row => row.data.PermitId),
                            PermitNamesForDraftData: refinedCriteria.PermitNamesForDraftData,
                            PermitNamesForCurrentData: refinedCriteria.PermitNamesForCurrentData,
                            isEfile: isEfile
                        } as IVolunteerReportCriteria).subscribe((reportId: number) => {
                            if (!stay) {
                                this.closeAll();
                                Dialogs.navigate(`/reporting/reports/volunteer/${reportId}`);
                                return;
                            }

                            this.reportIdJustCreated = reportId;
                            this.showReportJustCreatedBadge = true;

                            if (this.showNewReportBadgeSubscription !== undefined) {
                                this.showNewReportBadgeSubscription.unsubscribe();
                            }

                            this.showNewReportBadgeSubscription = timer(3500).subscribe(() => {
                                this.showReportJustCreatedBadge = false;
                            });
                        });
                    };

                    const alertBody = `The permits in the following list do not have saved draft.  This report request will use Current Data for these permits.
                                       <br/><textarea readonly style='margin-top: 8px; width: 90%; height: 100px;'>${refinedCriteria.NoDraftsPermit.join('\r\n')}</textarea>`;

                    if (refinedCriteria.NoDraftsPermit.length > 0) {
                        Dialogs.Alert({
                            Title: 'Volunteer Report',
                            Body: alertBody
                        }, () => {
                            delegate();
                        });
                    }
                    else {
                        delegate();
                    }
                });
        }
        else {
            this.reportService.RunVolunteerReport({
                PermitIdsForCurrentData: getCurrentDataPermits.map(row => row.data.PermitId),
                PermitNamesForDraftData: [],
                PermitNamesForCurrentData: getCurrentDataPermits.map(row => row.data.AgencyName),
                PermitIdsForDraftData: [],
                isEfile: isEfile
            } as IVolunteerReportCriteria).subscribe((reportId: number) => {
                if (!stay) {
                    this.closeAll();
                    Dialogs.navigate(`/reporting/reports/volunteer/${reportId}`);
                    return;
                }

                this.reportIdJustCreated = reportId;
                this.showReportJustCreatedBadge = true;

                if (this.showNewReportBadgeSubscription !== undefined) {
                    this.showNewReportBadgeSubscription.unsubscribe();
                }

                this.showNewReportBadgeSubscription = timer(3500).subscribe(() => {
                    this.showReportJustCreatedBadge = false;
                });
            });
        }
    }

    public validateCanSearchForPermits(): boolean {
        const hasCrop = this.availableCrops.filter(item => item.IsChecked).length > 0;
        const hasYear = this.availableYears.filter(item => item.IsChecked).length > 0;
        const hasStatus = this.availableStatus.filter(item => item.IsChecked).length > 0;

        return hasCrop && hasYear && hasStatus;
    }

    public validateCanRunReport(): boolean {
        return (this.selectedRowCount > 0);
    }

    public searchForPermits(): void {
        this.isSearching = true;
        this.totalRowCount = 0;
        this.gridOptions.api.setRowData([]);

        DataGrid.ShowLoading(this.gridOptions, 'Searching...');

        this.permitService.Search({
            Crops: this.availableCrops.filter(item => item.IsChecked).map(item => item.Value),
            Years: this.availableYears.filter(item => item.IsChecked).map(item => item.Value),
            Statuses: this.availableStatus.filter(item => item.IsChecked).map(item => item.Value),
            Countries: [539230],
            Types: ['FUFR', 'FURM', 'NFR', 'NFRM']
        } as IPermitSearch).subscribe((results: IVolunteerReportPermit[]) => {
            this.hasSearched = true;
            this.isSearching = false;
            this.totalRowCount = results.length;
            results.forEach(p => p.GetCurrentData = true);
            this.gridOptions.api.setRowData(results);

            if (this.totalRowCount === 0) {
                DataGrid.ShowLoading(this.gridOptions, 'No Permits Found', true);
            }
            else {
                DataGrid.HideLoading(this.gridOptions);
            }
        });
    }

    public CheckBoxClicked(e): void {
        if (this.availableCrops != null) {
            this.availableCrops.forEach(p => p.IsChecked = true);
        }
    }

    public open(closeAll = false) {
        this.onOpen.emit(false);
        this.modal.open();

        this.totalRowCount = 0;
        this.hasSearched = false;
        this.setupButtons();
    }

    onDestroy() {
        this.clearButtons();
        this.gridOptions.api.setRowData([]);
    }

    canCancel(): boolean {
        return true;
    }
}
